import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  DocumentFolderView, LibraryDocumentView, LibraryVersionView, PolicyAcknowledgementView,
  PolicyCampaignView, RetentionReviewView, RetentionRuleView,
} from '../types'

const BASE = '/v1/library'
const CAMPAIGNS = '/v1/policy-campaigns'

export interface LibraryListParams {
  page?: number
  size?: number
  sort?: string
  q?: string
  folderId?: string
  kind?: string
  status?: string
}

export function useLibrary(params: LibraryListParams) {
  return useQuery({
    queryKey: ['library', 'list', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<LibraryDocumentView>>(BASE, {
        params: {
          page: params.page,
          size: params.size,
          sort: params.sort,
          q: params.q || undefined,
          folderId: params.folderId || undefined,
          kind: params.kind || undefined,
          status: params.status || undefined,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useLibraryDocument(id: string | undefined) {
  return useQuery({
    queryKey: ['library', 'one', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<LibraryDocumentView>(`${BASE}/${id}`)).data,
  })
}

export function useLibraryVersions(id: string | undefined) {
  return useQuery({
    queryKey: ['library', 'versions', id],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<LibraryVersionView[]>(`${BASE}/${id}/versions`)).data,
  })
}

export function useFolders() {
  return useQuery({
    queryKey: ['library', 'folders'],
    queryFn: async () => (await apiClient.get<DocumentFolderView[]>(`${BASE}/folders`)).data,
  })
}

export function useRetentionRules() {
  return useQuery({
    queryKey: ['library', 'retention-rules'],
    queryFn: async () => (await apiClient.get<RetentionRuleView[]>(`${BASE}/retention-rules`)).data,
  })
}

export function useRetentionReview() {
  return useQuery({
    queryKey: ['library', 'retention-review'],
    queryFn: async () =>
      (await apiClient.get<RetentionReviewView[]>(`${BASE}/retention-review`)).data,
  })
}

export function usePolicyCampaigns() {
  return useQuery({
    queryKey: ['library', 'campaigns'],
    queryFn: async () => (await apiClient.get<PolicyCampaignView[]>(CAMPAIGNS)).data,
  })
}

export function useCampaignAcknowledgements(id: string | undefined) {
  return useQuery({
    queryKey: ['library', 'campaigns', id, 'acknowledgements'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<PolicyAcknowledgementView[]>(
        `${CAMPAIGNS}/${id}/acknowledgements`)).data,
  })
}

export function useLibraryMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['library'] })

  return {
    saveFolder: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<DocumentFolderView>(`${BASE}/folders/${id}`, body)).data
          : (await apiClient.post<DocumentFolderView>(`${BASE}/folders`, body)).data,
      onSuccess: invalidate,
    }),
    saveRetentionRule: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<RetentionRuleView>(`${BASE}/retention-rules`, body)).data,
      onSuccess: invalidate,
    }),
    saveDocument: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<LibraryDocumentView>(`${BASE}/${id}`, body)).data
          : (await apiClient.post<LibraryDocumentView>(BASE, body)).data,
      onSuccess: invalidate,
    }),
    publish: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<LibraryVersionView>(`${BASE}/${id}/versions`, body)).data,
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await apiClient.post<LibraryDocumentView>(`${BASE}/${id}/archive`, { reason })).data,
      onSuccess: invalidate,
    }),
    openCampaign: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<PolicyCampaignView>(CAMPAIGNS, body)).data,
      onSuccess: invalidate,
    }),
    acknowledge: useMutation({
      mutationFn: async ({ id, employeeId }: { id: string; employeeId: string }) =>
        (await apiClient.post<PolicyAcknowledgementView>(
          `${CAMPAIGNS}/${id}/acknowledge`, { employeeId })).data,
      onSuccess: invalidate,
    }),
    closeCampaign: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<PolicyCampaignView>(`${CAMPAIGNS}/${id}/close`)).data,
      onSuccess: invalidate,
    }),
  }
}
